import React, { useEffect, useState, useRef } from "react";
import {
  ChevronDown,
  Search,
  User,
  Menu,
  X,
  LogIn,
  Loader,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Scrollbars } from "react-custom-scrollbars-2";
import styles from "./CSS/Header.module.css";
import useAuthStore from "../../store/authStore";
import { useAuth } from "../../hooks/useAuth";

const Header = () => {
  const [isAudioOpen, setIsAudioOpen] = useState(false);
  const [isReadingOpen, setIsReadingOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);

  const audioDropdownRef = useRef(null);
  const audioButtonRef = useRef(null);
  const readingDropdownRef = useRef(null);
  const readingButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();
  const { signout, isLoading } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const handleClickOutside = (event) => {
      const userDrawerElement = document.querySelector('[data-drawer="user"]');
      const iconButtonElement = document.querySelector('[data-button="user"]');
      
      // Close user drawer when clicking outside
      if (
        isUserDrawerOpen &&
        !userDrawerElement?.contains(event.target) &&
        !iconButtonElement?.contains(event.target)
      ) {
        setIsUserDrawerOpen(false);
      }

      // Close audio dropdown when clicking outside
      if (
        isAudioOpen &&
        !audioDropdownRef.current?.contains(event.target) &&
        !audioButtonRef.current?.contains(event.target)
      ) {
        setIsAudioOpen(false);
      }

      // Close reading dropdown when clicking outside
      if (
        isReadingOpen &&
        !readingDropdownRef.current?.contains(event.target) &&
        !readingButtonRef.current?.contains(event.target)
      ) {
        setIsReadingOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isUserDrawerOpen, isAudioOpen, isReadingOpen]);

  const navItems = [
    { name: "Home", path: "/" },
    {
      name: "Audio",
      isDropdown: true,
      items: [
        { name: "Hindi", path: "/shortstories/hindi" },
        { name: "Garhwali", path: "/shortstory/garhwali" },
        { name: "English", path: "/shortstory/english" },
      ],
    },
    {
      name: "Reading",
      isDropdown: true,
      items: [
        { name: "Hindi", path: "/hindi-reading" },
        { name: "Garhwali", path: "/garhwali-reading" },
        { name: "English", path: "/reading" },
      ],
    },
    { name: "Video", path: "/video" },
  ];

  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsAudioOpen(false);
    setIsReadingOpen(false);
    setIsUserDrawerOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signout();
      setIsUserDrawerOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  };

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const isAudioActive = () => {
    return ["/audiobook", "/audioseries", "/shortstories"].some((path) =>
      location.pathname.startsWith(path)
    );
  };

  const isReadingActive = () => {
    return ["/reading", "/hindi-reading"].some((path) =>
      location.pathname.startsWith(path)
    );
  };

  const UserDrawer = () => (
    <>
      <div
        className={`${styles.drawerOverlay} ${
          isUserDrawerOpen ? styles.open : ""
        }`}
        onClick={() => setIsUserDrawerOpen(false)}
      />
      <div
        className={`${styles.userDrawer} ${
          isUserDrawerOpen ? styles.open : ""
        }`}
        data-drawer="user"
      >
        <button
          className={styles.closeDrawer}
          onClick={() => setIsUserDrawerOpen(false)}
        >
          <X size={24} />
        </button>
        <Scrollbars style={{ width: "100%", height: "100%" }}>
          <div className={styles.drawerContent}>
            <div className={styles.userInfo}>
              <div className={styles.userAvatar}>
                <User size={48} />
              </div>
              <div className={styles.userDetails}>
                <h3>{user?.name || "User Name"}</h3>
                <p>{user?.email || "user@example.com"}</p>
              </div>
            </div>
            <ul className={styles.drawerMenu}>
              <li onClick={() => handleNavigate("/account")}>
                <span>My Account</span>
              </li>
              {(user?.role === "admin" || user?.role === "super_admin") && (
                <li onClick={() => handleNavigate("/admin/dashboard")}>
                  <span>Go To Dashboard</span>
                </li>
              )}
              <li onClick={() => handleNavigate("/playlists")}>
                <span>Playlists</span>
              </li>
              <li onClick={() => handleNavigate("/likes")}>
                <span>Like List</span>
              </li>
              <li onClick={() => handleNavigate("/following")}>
                <span>My Following</span>
              </li>
              <li onClick={handleSignOut} className={styles.signOutItem}>
                {isLoading ? (
                  <div className={styles.loadingWrapper}>
                    <Loader className={styles.loadingIcon} />
                    <span>Signing out...</span>
                  </div>
                ) : (
                  <span>Sign Out</span>
                )}
              </li>
            </ul>
          </div>
        </Scrollbars>
      </div>
    </>
  );

  return (
    <header className={`${styles.header} ${isScrolled ? styles.scrolled : ""}`}>
      <nav className={styles.navContainer}>
        <div className={styles.navContent}>
          {/* Logo */}
          <div className={styles.logo}>
            <img
              src="/images/logo/newlogo.png"
              alt="logo"
              className={styles.logoPic}
              onClick={() => handleNavigate("/")}
            />
          </div>

          {/* Desktop Navigation */}
          <div className={styles.desktopNav}>
            {navItems.map((item) => (
              <div
                key={item.name}
                className={item.isDropdown ? styles.dropdown : ""}
              >
                {item.isDropdown ? (
                  <div>
                    {item.name === "Audio" ? (
                      <button
                        ref={audioButtonRef}
                        className={`${styles.dropdownButton} ${
                          isAudioActive() ? styles.active : ""
                        }`}
                        onClick={() => {
                          setIsAudioOpen(!isAudioOpen);
                          setIsReadingOpen(false);
                        }}
                      >
                        {item.name}
                        <ChevronDown className={styles.chevronIcon} />
                      </button>
                    ) : (
                      <button
                        ref={readingButtonRef}
                        className={`${styles.dropdownButton} ${
                          isReadingActive() ? styles.active : ""
                        }`}
                        onClick={() => {
                          setIsReadingOpen(!isReadingOpen);
                          setIsAudioOpen(false);
                        }}
                      >
                        {item.name}
                        <ChevronDown className={styles.chevronIcon} />
                      </button>
                    )}
                    {item.name === "Audio" && isAudioOpen && (
                      <div
                        ref={audioDropdownRef}
                        className={`${styles.dropdownContent} ${
                          isAudioOpen ? styles.active : ""
                        }`}
                      >
                        {item.items.map((subItem) => (
                          <button
                            key={subItem.name}
                            onClick={() => handleNavigate(subItem.path)}
                            className={`${styles.dropdownItem} ${
                              isActive(subItem.path) ? styles.active : ""
                            }`}
                          >
                            {subItem.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {item.name === "Reading" && isReadingOpen && (
                      <div
                        ref={readingDropdownRef}
                        className={`${styles.dropdownContent} ${
                          isReadingOpen ? styles.active : ""
                        }`}
                      >
                        {item.items.map((subItem) => (
                          <button
                            key={subItem.name}
                            onClick={() => handleNavigate(subItem.path)}
                            className={`${styles.dropdownItem} ${
                              isActive(subItem.path) ? styles.active : ""
                            }`}
                          >
                            {subItem.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavigate(item.path)}
                    className={`${styles.navItem} ${
                      isActive(item.path) ? styles.active : ""
                    }`}
                  >
                    {item.name}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Icons Container */}
          <div className={styles.iconsContainer}>
            <button
              className={styles.iconButton}
              onClick={() => handleNavigate("/search")}
            >
              <Search />
            </button>
            {isAuthenticated ? (
              <button
                className={styles.iconButton}
                onClick={() => setIsUserDrawerOpen(!isUserDrawerOpen)}
                data-button="user"
              >
                <User />
              </button>
            ) : (
              <button
                className={styles.iconButton}
                onClick={() => handleNavigate("/signin")}
              >
                <LogIn />
              </button>
            )}
            <button
              className={styles.mobileMenuButton}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* User Drawer */}
        {isAuthenticated && <UserDrawer />}

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className={`${styles.mobileMenu} ${
              isMobileMenuOpen ? styles.active : ""
            }`}
          >
            {navItems.map((item) => (
              <div key={item.name}>
                {item.isDropdown ? (
                  <div>
                    {item.name === "Audio" ? (
                      <button
                        className={`${styles.mobileNavItem} ${
                          isAudioActive() ? styles.active : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsAudioOpen(!isAudioOpen);
                          setIsReadingOpen(false);
                        }}
                      >
                        {item.name}
                        <ChevronDown className={styles.chevronIcon} />
                      </button>
                    ) : (
                      <button
                        className={`${styles.mobileNavItem} ${
                          isReadingActive() ? styles.active : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsReadingOpen(!isReadingOpen);
                          setIsAudioOpen(false);
                        }}
                      >
                        {item.name}
                        <ChevronDown className={styles.chevronIcon} />
                      </button>
                    )}
                    {item.name === "Audio" && isAudioOpen && (
                      <div className={styles.mobileDropdownContent}>
                        {item.items.map((subItem) => (
                          <button
                            key={subItem.name}
                            onClick={() => handleNavigate(subItem.path)}
                            className={`${styles.mobileDropdownItem} ${
                              isActive(subItem.path) ? styles.active : ""
                            }`}
                          >
                            {subItem.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {item.name === "Reading" && isReadingOpen && (
                      <div className={styles.mobileDropdownContent}>
                        {item.items.map((subItem) => (
                          <button
                            key={subItem.name}
                            onClick={() => handleNavigate(subItem.path)}
                            className={`${styles.mobileDropdownItem} ${
                              isActive(subItem.path) ? styles.active : ""
                            }`}
                          >
                            {subItem.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavigate(item.path)}
                    className={`${styles.mobileNavItem} ${
                      isActive(item.path) ? styles.active : ""
                    }`}
                  >
                    {item.name}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;